import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("mh_user") || "null"); } catch { return null; }
  });
  const [status, setStatus] = useState("loading"); // loading | authed | anon

  const persist = useCallback((token, u) => {
    if (token) localStorage.setItem("mh_token", token);
    if (u) localStorage.setItem("mh_user", JSON.stringify(u));
    setUser(u);
    setStatus("authed");
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("mh_token");
    localStorage.removeItem("mh_user");
    setUser(null);
    setStatus("anon");
  }, []);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("mh_token");
    if (!token) { setStatus("anon"); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      localStorage.setItem("mh_user", JSON.stringify(data.user));
      setStatus("authed");
    } catch {
      setStatus("anon");
    }
  }, []);

  useEffect(() => {
    // Skip refresh if we're mid-OAuth callback
    if (window.location.hash?.includes("session_id=")) { setStatus("loading"); return; }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    persist(data.token, data.user);
    return data;
  };
  const signup = async (email, password, name) => {
    const { data } = await api.post("/auth/signup", { email, password, name });
    persist(data.token, data.user);
    return data;
  };
  const googleExchange = async (sessionId) => {
    const { data } = await api.post("/auth/google/session", { session_token: sessionId });
    persist(data.token, data.user);
    return data;
  };

  return (
    <AuthCtx.Provider value={{ user, status, login, signup, googleExchange, logout, refresh, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
